<template>
  <div id="app" class="text-sm text-base-90" :class="{'backstage-mode': isBackstage}">
    <router-view />

    <modal-disconnection />
    <modal-invalid-token />

    <modal-error
      :title="errorOnInternalServer"
      v-if="errorOnInternalServer !== null"
      @hide="handleHideErrorModal"
    />

    <BackgroundEnlargeImage />

    <!-- This component can be located anywhere in your App. -->
    <!-- The slot content of the above portal component will be rendered here. -->
    <portal-target id="modal-destination" name="modal-destination" multiple />
    <portal-target name="context-menu-destination" />
    <portal-target name="report-panel-destination" style="top: 4rem;" />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import { app } from '@microsoft/teams-js'
import EventBus from '@/eventBus'
import socket from '@/socket'
import Config from '@/config'

// utils
import utils from '@/util'

// components
import BackgroundEnlargeImage from '@/components-v2/presentation-editor/middle/BackgroundEnlargeImage.vue'

// modals
import ModalDisconnection from '@/components-v2/modals/Disconnection.vue'
import ModalError from '@/components-v2/modals/Error.vue'
import ModalInvalidToken from '@/components-v2/modals/InvalidToken.vue'

// api
import Api from '@/api'
import UserApi from '@/api/user'
import PresentationApi from '@/api/presentation'
// services
import cookie from '@/services/cookie'
import { initIndexedDB } from './utils/indexedDB/indexed.db'
import constant from './constant'
import * as localStorageExpiry from '@/utils/local.storage.expiry'

initIndexedDB((e) => {
  Sentry.captureException(e)
})

export default {
  components: {
    ModalDisconnection,
    ModalError,
    ModalInvalidToken,
    BackgroundEnlargeImage,
  },
  data() {
    return {
      connectSocket: false,
    }
  },
  async created() {
    const hasMSTeamsInPathOrParam = window.location.href.includes('ms-teams') || window.location.href.includes('ms_teams')
    if (hasMSTeamsInPathOrParam) {
      await app.initialize()
      app.notifySuccess()
      this.$store.state.isMSTeamsApp = true
    }
  },
  computed: {
    ...mapState({
      presentations: (state) => state.presentations,
      slideActive: (state) => state.slideActive,
      isScreenCapture: (state) => state.isScreenCapture,
      currentUser: (state) => state.currentUser,
      errorOnInternalServer: (state) => state.errors.errorOnInternalServer,
    }),
    ...mapGetters(['isBackstage']),
  },
  watch: {
    '$store.getters.isPresenting': function handleIsPresentingChange() {
      this.checkWindowResize()
    },
  },
  async mounted() {
    this.setUserFirstPage()
    const urlParams = new URLSearchParams(window.location.search)
    const isAdmin = urlParams.get('admin')

    // setup vue resource in order to make AJAX requests
    Api.initResources()

    this.$store.state.isPresenterWhiteLabel = utils.checkWhiteLabelDomain()

    socket.initialize()
    socket.on('connect', this.handleSocketConnect)

    EventBus.$on('detectScreen', (force) => this.checkWindowResize(force))
    window.addEventListener('resize', () => this.checkWindowResize())

    if (isAdmin) {
      this.$store.state.isAdmin = true
      return
    }

    if (this.$store.state.isPresenterWhiteLabel) {
      utils.mixpanelTracking('White Label', 'Open', `Host: ${window.location.host}`)
    }

    if (!cookie.getValue('ahaToken')) return

    const user = await UserApi.getUser()
    this.getCurrentUserAction()
    socket.emit('join-user', `user-${user.id}`)
    socket.emit('join-user', 'ahaslides-presenter')
    socket.subscribe(`user-${user.id}`, ({
      action, type, socketId, data,
    }) => {
      if (type === 'user' && action === 'accountRemoval') {
        // in case presenter is opening multiple tabs
        // the active tab must not log out in order to show comfirmation popup
        if (socketId === socket.getSocketId()) return

        this.logOutAccountRemoval()
      }
      if (type === 'notification' && action === 'new-notification') {
        this.$store.state.notifications.unshift(data)
        this.$store.state.notificationUnread++
      }
    })
    socket.subscribe('ahaslides-presenter', ({
      action, type, data,
    }) => {
      if (type === 'notification' && action === 'new-notification') {
        this.$store.state.notifications.unshift(data)
        this.$store.state.notificationUnread++
      }
    })
    this.checkUserHasHostedEvents()
    utils.ga4Config({
      user_id: user.id,
    })
    this.setFlagUserUpdatedGA4(user)
  },
  methods: {
    ...mapActions([
      'logOutAccountRemoval',
      'setErrorOnInternalServerAction',
      'setCurrentUserAction',
      'getCurrentUserAction',
    ]),

    setFlagUserUpdatedGA4(user) {
      if (!user || user.isGuest) {
        return
      }
      const keyFlag = `updated-ga4-${user.id}`
      const updatedGA4 = localStorageExpiry.getWithExpiry(keyFlag)
      if (!updatedGA4) {
        utils.ga4Set('user_properties', {
          first_page: user.firstPage,
          referral_user_id: user.referralUserId,
          sign_up_source: user.source,
          use_ahaslides_for: user.personalInformation ? user.personalInformation.useAhaSlidesFor : null,
          updated_at: user.updatedAt,
        })
        localStorageExpiry.setWithExpiry(keyFlag, true, 30 * 24 * 60 * 60 * 1000)
      }

      const keyFlagUpdateSource = `updated-ga4-user-source-${user.id}`
      const updatedUserSourceGA4 = localStorageExpiry.getWithExpiry(keyFlagUpdateSource)
      if (!updatedUserSourceGA4) {
        utils.ga4Set('user_properties', {
          sign_up_source: user.source,
        })
        localStorageExpiry.setWithExpiry(keyFlagUpdateSource, true, 3650 * 24 * 60 * 60 * 1000)
      }
    },
    setUserFirstPage() {
      const getCookie = function (name) {
        const value = `; ${document.cookie}`
        const parts = value.split(`; ${name}=`)
        if (parts.length === 2) {
          return parts.pop().split(';').shift()
        }
        return null
      }
      const setCookie = function (name, value) {
        const cookieName = name
        const cookieValue = value
        const myDate = new Date()
        myDate.setMonth(myDate.getMonth() + 12)
        document.cookie = `${cookieName}=${cookieValue};expires=${myDate};domain=.ahaslides.com;path=/`
        document.cookie = `${cookieName}=${cookieValue};expires=${myDate};domain=.ahaslide.com;path=/`
        document.cookie = `${cookieName}=${cookieValue};expires=${myDate};domain=localhost;path=/`
      }
      const firstPage = getCookie('ahaFirstPage')
      const ahaToken = getCookie('ahaToken')
      const isGuest = getCookie('aha-guest-user')
      if (firstPage || (ahaToken && !isGuest)) {
        return
      }
      setCookie('ahaFirstPage', window.location.href)
    },
    async handleSocketConnect() {
      const presentationId = this.$store.state.presentation.id

      // THIS BLOCK PROBABLY SHOULD BE REMOVED OR MOVED INTO PresentationEditor.vue
      // into handleOnConnect method
      if (
        presentationId
        && this.connectSocket
        && !this.$store.state.sharePresentation
      ) {
        const payload = { socketId: socket.getSocketId() }

        if (this.$store.state.isAdmin) {
          payload.admin = true
        }

        let res
        try {
          res = await this.$http.get(Config.api.getPresentationDetail + presentationId, {
            params: payload,
          })
        } catch (error) {
          Sentry.captureException(error)
          return
        }

        const presentation = utils.formatPresentationReconect(res.body)
        this.mergeDataPresentation(presentation)

        return
      }
      // END OF BLOCK

      this.connectSocket = true
    },
    mergeDataPresentation(data) {
      const { slides } = this.$store.state.presentation
      slides.forEach((slide) => {
        const index = data.slides.findIndex((item) => {
          return item.id === slide.id
        })
        if (slide.id && index !== -1) {
          if (slide.type === 'openEnded' || slide.type === 'voiceQA' || slide.type === 'wordCloud') {
            slide.SlideOptions = data.slides[index].SlideOptions
          } else if (slide.type === 'multipleChoice' || slide.type === 'imageChoice') {
            slide.SlideOptions.forEach((option) => {
              const indexOption = data.slides[index].SlideOptions.findIndex(
                (item) => {
                  return item.id === option.id
                },
              )

              if (option.id && indexOption !== -1) {
                option.count =
                  data.slides[index].SlideOptions[indexOption].count
              }
            })

            if (slide.id === this.$store.state.slideActive.id) {
              this.$store.dispatch('updateSlideActive', slide)
            }
          }
        }
      })
      this.$store.dispatch(
        'updatePresentation',
        this.$store.state.presentation,
      )
    },

    checkWindowResize(force = false) {
      // let wHeight = window.innerHeight - (window.innerHeight / 100) * 8;

      EventBus.$emit('window-resizeing')
      let height
      let width
      const mainSlide = document.getElementById('main-slide')
      const containerSlide = document.getElementById('slide-container')
      let scale = 1
      const fixWidth = 1280
      const fixHeight = 720
      let currentWidth
      let currentHeight

      if (!mainSlide) return
      this.$store.state.currentSlideCanvas = 'screen-hd'

      if (!this.$store.getters.isPresenting) {
        // in editor mode, set correct height so that
        // canvas + buttons + notes fit into the screen without scrolling
        const HEADER_HEIGHT = 64
        const CANVAS_PADDING_TOP = 16
        const BUTTONS_PLUS_NOTES_HEIGHT = 215
        const OFFSET = 10
        height = window.innerHeight - BUTTONS_PLUS_NOTES_HEIGHT
         - HEADER_HEIGHT - CANVAS_PADDING_TOP - OFFSET

        width = window.innerWidth - 632
      } else {
        height = window.innerHeight
        width = window.innerWidth
      }

      if (this.$store.state.sharePresentation && !utils.getParamValue('hideText', window.location.href)) {
        height -= 100
      }

      if (this.isBackstage) {
        width = window.innerWidth - 420
        height = window.innerHeight
      }

      const ratio = width / height

      if (ratio > 16 / 9) {
        currentWidth = (height * 16) / 9
        currentHeight = height
      } else {
        currentHeight = (width * 9) / 16
        currentWidth = width
      }
      scale = currentHeight / fixHeight
      mainSlide.style.width = `${currentWidth}px`
      containerSlide.style.width = `${fixWidth}px`
      containerSlide.style.transform = `scale(${scale})`
      this.$store.state.slideContainerScale = scale
      if (force || (!this.isScreenCapture || this.slideActive.type !== 'openEnded')) {
        mainSlide.style.height = `${currentHeight}px`
        containerSlide.style.height = `${fixHeight}px`
      }
    },

    handleHideErrorModal() {
      if (this.errorOnInternalServer === constant.errors.GOOGLE_LOGIN_ERROR) {
        this.$router.replace({ name: 'Login' })
      }
      if (this.errorOnInternalServer === constant.errors.SKOLETUBE_LOGIN_ERROR) {
        this.$func.go(Config.skoletubeHomepage)
      }
      this.setErrorOnInternalServerAction(null)
    },

    async checkUserHasHostedEvents() {
      if (!this.currentUser.firstEventStartedAt) {
        const numOfHostedEvents = await PresentationApi.countHostedEvents()
        if (numOfHostedEvents > 0) {
          this.$store.state.userHasHostedEvents = true
        }
      }
    },

  },
}
</script>

<style lang="scss" scoped>
.backstage-mode {
  &::v-deep {
    .editor-header {
      background-color: #191F2D;
      .presentation-title-container .text-secondary-100, .backstage-buttons .tab-trigger-btn:hover {
        color: #ffffff;
      }
      .presentation-title-container {
        cursor: default;
      }
      .backstage-buttons .tab-trigger-btn {
        color: #B4BCCF;
      }
      .private-mode-button {
        background-color: #3F4557;
        .private-mode-icon {
          color: #ffffff;
        }
      }
    }
    .aha-side-left {
      background-color: #282E3E;
      border-color: #3F4557;
      .list-group-item {
        &.active-slide, &.select-slide {
          background-color: #191F2D;
          .aha-slide-order {
            color: #348EF6;
          }
          .aha-slide-content {
            box-shadow: 0 0 0 3px #4f9af2;
          }
        }
        .aha-slide-order {
          color: #ffffff;
        }
        .aha-slide-content:hover {
          box-shadow: 0 0 0 3px #4f9af2;
        }
      }
    }
    .aha-middle, .aha-slide-notes .notes-input {
      background-color: #282E3E;
      border-color: #3F4557;
      color: #ffffff;
    }
    .aha-slide-notes {
      min-height: 140px;
      padding-top: 1.5rem;
      .notes-input {
        font-size: 18px;
      }
    }
  }
}
</style>
